<template>
  <div class="instruments-container">
    <div class="page-title">仪器设备列表</div>

    <!-- 搜索和筛选区域 -->
    <div class="filter-bar">
      <el-input 
        v-model="searchQuery" 
        placeholder="搜索仪器名称..." 
        prefix-icon="Search" 
        clearable
        class="search-input"
      ></el-input>
      <el-button size="large" @click="searchByName">搜索</el-button>

      <el-select 
        v-model="selectedUnit" 
        placeholder="选择所属平台" 
        clearable 
        class="filter-select"
        @change="handleSelectChange" 
      >
        <el-option 
          v-for="unit in platforms" 
          :key="unit.namePlatform" 
          :label="unit.namePlatform" 
          :value="unit.namePlatform"
        ></el-option>
      </el-select>
    </div>

    <!-- 加载状态（优化：非全屏加载，避免遮挡筛选栏） -->
    <div v-if="loading" class="loading-container">
      <el-loading type="circle" text="正在加载仪器信息..."></el-loading>
    </div>

    <!-- 错误提示 -->
    <el-alert 
      v-if="error" 
      title="数据加载失败" 
      :description="error" 
      type="error" 
      show-icon 
      class="error-alert"
    ></el-alert>

    <!-- 仪器列表 -->
    <div class="instruments-list" v-if="!loading && !error">
      <!-- 无数据提示（修复：正确判断空数据） -->
      <div v-if="instruments.length === 0" class="no-results">
        <el-empty description="没有找到匹配的仪器"></el-empty>
      </div>

      <!-- 仪器卡片网格 -->
      <el-row :gutter="20" v-else>
        <el-col 
          :xs="24" 
          :sm="12" 
          :md="6" 
          :lg="6" 
          :xl="6" 
          v-for="instrument in instruments"
          :key="instrument.instrumentNo" 
          class="card-col"
        >
          <instrument-card :instrument="instrument" />
        </el-col>
      </el-row>

      <!-- 分页控件 -->
      <div class="pagination">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.currentPage"
          :page-sizes="[12, 24, 36]"
          :page-size="pagination.pageSize"
          layout="total, prev, pager, next,sizes"
          :total="pagination.total"
        ></el-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import instrumentCard from './instrumentCard.vue';
import { sliceImgUrl } from '@/utils/MyUtils.js'

export default {
  name: 'InstrumentsList',
  components: { instrumentCard },
  data() {
    return {
      // 搜索和筛选参数（页面切换时保留）
      searchQuery: '',
      selectedUnit: '', // 所属平台筛选
      // 数据相关
      instruments: [],
      loading: true,
      error: null,
      platforms: [], // 平台列表
      // 分页配置（合并为对象，统一管理）
      pagination: {
        currentPage: 1,
        pageSize: 12,
        total: 0
      }
    };
  },
  computed: {
    // 核心：动态拼接组合查询URL（分页+仪器名称搜索+平台筛选）
    baseUrl() {
      const { currentPage, pageSize } = this.pagination;
      let url = `/instrument/page?pageNum=${currentPage}&pageSize=${pageSize}`;
      
      // 拼接仪器名称搜索参数（模糊查询）
      if (this.searchQuery.trim()) {
        url += `&nameKeyword=${encodeURIComponent(this.searchQuery.trim())}`;
      }
      // 拼接所属平台筛选参数（精确查询）
      if (this.selectedUnit) {
        url += `&platformName=${encodeURIComponent(this.selectedUnit)}`;
      }
      return url;
    }
  },
  watch: {
    // 监听搜索关键词变化：重置页码+加载数据
    searchQuery: {
      handler() {
        this.pagination.currentPage = 1;
        this.loadData(this.baseUrl);
      },
      immediate: false
    },
    // 监听平台筛选变化：重置页码+加载数据
    selectedUnit: {
      handler() {
        this.pagination.currentPage = 1;
        this.loadData(this.baseUrl);
      },
      immediate: false
    },
    // 监听路由变化：页面切换后重新加载数据（保留筛选状态）
    $route: {
      handler() {
        this.loadData(this.baseUrl);
      },
      immediate: false
    }
  },
  mounted() {
    // 并行加载平台列表和仪器数据（优化性能）
    Promise.all([this.getPlatform(), this.loadData(this.baseUrl)])
      .catch(err => {
        console.error('初始化数据加载失败:', err);
      });
  },
  methods: {
    // 获取所有平台（填充下拉框）
    async getPlatform() {
      try {
        const response = await this.$http.get("/instrumentPlatform");
        this.platforms = response.data || [];
        this.error = null;
      } catch (err) {
        this.error = '平台数据加载失败，请稍后重试';
        console.error('加载仪器平台数据出错:', err);
      }
    },
    // 加载仪器数据（统一处理组合查询+分页）
    async loadData(url) {
      this.loading = true;
      try {
        const response = await this.$http.get(url);
        this.pagination.total = response.data.total || 0;
        this.instruments = response.data.records || [];
        sliceImgUrl(this.instruments); // 处理图片URL（原有逻辑保留）
        this.error = null;
      } catch (err) {
        this.error = '仪器数据加载失败，请稍后重试';
        this.instruments = [];
        console.error('加载仪器数据出错:', err);
      } finally {
        this.loading = false;
      }
    },
    // 每页数量变化
    handleSizeChange(size) {
      this.pagination.pageSize = size;
      this.pagination.currentPage = 1;
      this.loadData(this.baseUrl);
    },
    // 当前页码变化
    handleCurrentChange(page) {
      this.pagination.currentPage = page;
      this.loadData(this.baseUrl);
      // 滚动到顶部（优化用户体验）
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    // 搜索按钮点击（与watch逻辑一致，避免重复代码）
    searchByName() {
      this.pagination.currentPage = 1;
      this.loadData(this.baseUrl);
    },
    // 平台筛选变化（直接赋值，watch自动触发加载）
    handleSelectChange(value) {
      this.selectedUnit = value; // 清空筛选时value为undefined，自动查全部
    }
  }
};
</script>

<style scoped>
.instruments-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 30px;
}

.card-col {
  padding-bottom: 20px; /* 优化卡片底部间距 */
}

.page-title {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 30px;
  color: #333;
}

/* 筛选区域：靠左排列，优化对齐 */
.filter-bar {
  display: flex;
  gap: 15px;
  margin-bottom: 30px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
}

.search-input {
  width: 300px;
  min-width: 180px;
}

.filter-select {
  width: 200px;
}

.instruments-list {
  margin-top: 20px;
  position: relative; /* 为加载中状态定位 */
}

/* 加载中样式（非全屏，居中显示） */
.loading-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  padding: 40px;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
}

.no-results {
  text-align: center;
  padding: 80px 0;
}

.pagination {
  margin-top: 40px;
  text-align: center;
}

.error-alert {
  margin-bottom: 30px;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .instruments-container {
    max-width: 95%;
    padding: 15px;
  }
  
  .filter-bar {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-input, .filter-select {
    width: 100%;
  }
  
  .loading-container {
    padding: 20px;
  }
}
</style>